import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Eye, EyeOff, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload, { GalleryUpload } from "@/components/admin/ImageUpload";
import RichTextEditor from "@/components/admin/RichTextEditor";

const EMPTY = {
    title: "", slug: "", category: "", location: "", surface: "", year: "",
    summary: "", description: "", before_image: "", after_image: "",
    gallery: [], client_quote: "", featured: false, published: false,
};

export default function AdminProjects() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(undefined);
    const [form, setForm] = useState(EMPTY);

    const load = async () => {
        try { const { data } = await api.get("/admin/projects"); setItems(data); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const open = (p = null) => {
        setEditing(p ?? null);
        setForm(p ? { ...p, gallery: Array.isArray(p.gallery) ? p.gallery : [] } : EMPTY);
    };
    const close = () => { setEditing(undefined); setForm(EMPTY); };

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, gallery: Array.isArray(form.gallery) ? form.gallery : [] };
            if (editing && editing.id) {
                const { data } = await api.put(`/admin/projects/${editing.id}`, payload);
                setItems(items.map((i) => (i.id === data.id ? data : i)));
                toast.success("Projet modifié");
            } else {
                const { data } = await api.post("/admin/projects", payload);
                setItems([data, ...items]);
                toast.success("Projet créé");
            }
            close();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const togglePublished = async (p) => {
        try {
            const { data } = await api.put(`/admin/projects/${p.id}`, { ...p, gallery: p.gallery || [], published: !p.published });
            setItems(items.map((i) => (i.id === data.id ? data : i)));
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (p) => {
        if (!window.confirm(`Supprimer "${p.title}" ?`)) return;
        await api.delete(`/admin/projects/${p.id}`);
        setItems(items.filter((i) => i.id !== p.id));
    };

    return (
        <div className="p-10" data-testid="admin-projects-page">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <span className="overline">Espace admin</span>
                    <h1 className="mt-3 font-serif text-4xl text-brand-ink font-light">Réalisations</h1>
                </div>
                <button onClick={() => open()} data-testid="project-new" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-ink text-brand-bg text-xs uppercase tracking-[0.2em] hover:bg-brand-sage transition-colors">
                    <Plus size={14} /> Nouvelle réalisation
                </button>
            </div>
            {loading ? <p className="mt-12 text-brand-sageDark">Chargement…</p> :
             items.length === 0 ? (
                <div className="mt-12 py-16 text-center bg-brand-bgalt border border-brand-border">
                    <p className="font-serif text-xl text-brand-ink italic">Aucune réalisation publiée.</p>
                </div>
             ) : (
                <div className="mt-10 bg-brand-bgalt border border-brand-border divide-y divide-brand-border">
                    {items.map((p) => (
                        <div key={p.id} className="p-5 flex items-center justify-between gap-4">
                            {p.after_image ? <img src={p.after_image} alt="" className="w-16 h-16 object-cover" /> : <div className="w-16 h-16 bg-brand-bg/40" />}
                            <div className="flex-1 min-w-0">
                                <p className="font-serif text-lg text-brand-ink truncate">{p.title}</p>
                                <p className="text-xs text-brand-sageDark">{[p.location, p.surface, p.year].filter(Boolean).join(" · ") || "—"}</p>
                            </div>
                            <span className={`text-xs uppercase tracking-[0.16em] px-3 py-1 ${p.published ? "bg-brand-sage text-brand-bg" : "border border-brand-border text-brand-sageDark"}`}>{p.published ? "Publié" : "Brouillon"}</span>
                            <div className="flex items-center gap-1">
                                <button onClick={() => togglePublished(p)} className="p-2 hover:bg-brand-sage/10">{p.published ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                                <button onClick={() => open(p)} className="p-2 hover:bg-brand-sage/10"><Pencil size={14} /></button>
                                <button onClick={() => remove(p)} className="p-2 hover:bg-red-50 text-red-700"><Trash2 size={14} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {editing !== undefined && (
                <div className="fixed inset-0 z-50 bg-brand-ink/60 flex items-center justify-center px-4 py-8 overflow-y-auto">
                    <form onSubmit={save} className="bg-brand-bg max-w-3xl w-full p-8 md:p-10 border border-brand-border relative space-y-5 my-auto">
                        <button type="button" onClick={close} className="absolute top-4 right-4 text-brand-sageDark"><X size={18} /></button>
                        <h2 className="font-serif text-2xl text-brand-ink font-light">{editing ? "Modifier" : "Nouvelle"} réalisation</h2>
                        <div>
                            <label className="overline block mb-2">Titre *</label>
                            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="field-input" />
                        </div>
                        <div className="grid md:grid-cols-3 gap-4">
                            <div><label className="overline block mb-2">Catégorie</label><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="field-input" placeholder="Appartement" /></div>
                            <div><label className="overline block mb-2">Lieu</label><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="field-input" placeholder="Paris 16e" /></div>
                            <div><label className="overline block mb-2">Surface</label><input value={form.surface} onChange={(e) => setForm({ ...form, surface: e.target.value })} className="field-input" placeholder="142 m²" /></div>
                            <div><label className="overline block mb-2">Année</label><input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="field-input" placeholder="2026" /></div>
                        </div>
                        <div><label className="overline block mb-2">Résumé (1 phrase)</label><input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="field-input" /></div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <ImageUpload label="Photo Avant" value={form.before_image} onChange={(url) => setForm({ ...form, before_image: url })} testId="project-before-upload" />
                            <ImageUpload label="Photo Après" value={form.after_image} onChange={(url) => setForm({ ...form, after_image: url })} testId="project-after-upload" />
                        </div>
                        <GalleryUpload label="Galerie de photos" value={form.gallery} onChange={(arr) => setForm({ ...form, gallery: arr })} testId="project-gallery-upload" />
                        <div>
                            <label className="overline block mb-2">Description détaillée</label>
                            <RichTextEditor value={form.description} onChange={(html) => setForm({ ...form, description: html })} placeholder="Présentez le projet, les défis, la solution apportée…" testId="project-description-editor" />
                        </div>
                        <div><label className="overline block mb-2">Citation client</label><textarea rows={2} value={form.client_quote} onChange={(e) => setForm({ ...form, client_quote: e.target.value })} className="field-input field-textarea" /></div>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-brand-sage" />
                            Publier (visible sur le site)
                        </label>
                        <div className="flex justify-end gap-3 pt-4">
                            <button type="button" onClick={close} className="px-5 py-2 text-xs uppercase tracking-[0.2em] text-brand-sageDark">Annuler</button>
                            <button type="submit" className="px-7 py-3 bg-brand-ink text-brand-bg text-xs uppercase tracking-[0.2em] hover:bg-brand-sage">Enregistrer</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
